import { formatDate, isAfter, isBefore, startOfDay } from "date-fns"
import { getSlotLabel } from "@/lib/slot"
import { combineCareProviderTitle } from "@/modules/care-provider/utils/combine-care-provider-title"
import { chTimeWindowFragment$data } from "@/modules/patients/fragments/ch/__generated__/chTimeWindowFragment.graphql"
import { PatientFragment$data } from "@/modules/patients/services/__generated__/PatientFragment.graphql"
import { Appointment } from "@/modules/patients/services/PatientAppointment"
import {
  AppointmentEdges,
  ChAlternativeTriageEdges,
  ChExternalAssistancesEdges,
  DeAlternativeTriageEdges,
  TimeWindowsType,
} from "@/modules/patients/services/usePatientAgenda"
import {
  Access,
  AppointmentStatus,
  CommunicationChannel,
  Language,
  ReferralReason,
  ReferredBy,
  Service,
  ServiceSpecialty,
} from "@/types/__generated__/union-types"

export type AppointmentAgenda = {
  __typename:
    | "Appointment"
    | "ch_TimeWindow"
    | "ch_AlternativeTriage"
    | "de_AlternativeTriage"
    | "ch_ExternalAssistance"
  id: string
  delay?: number
  dateSort: Date | null
  status?: AppointmentStatus
  assignedTo?: string | null
  language?: Language
  access?: Access
  service?: Service
  specialty?: ServiceSpecialty
  reason?: string
  phoneNumber?: string
  channel?: CommunicationChannel
  patient?: PatientFragment$data
  appointment?: Appointment
  dateFrom?: Date
  dateTo?: Date
  referralToCareProvider?: TimeWindowsType["referralToCareProvider"]
  createdAt?: string
  updatedBy?: string
  updatedAt?: string
  createdBy?: string
  completedBy?: string | null
  referredBy?: ReferredBy
  referralToCareProviderId?: string
  referralToDoctorName?: string
  referralReason?: ReferralReason | null
  internalComment?: string
  referredByExternalCareProviderId?: string
  progressNoteId?: string | null
  errorCorrection?: boolean | null
  referenceDate?: string
  alternativeTriage?: boolean | null
  alternativeTriageEntry?: chTimeWindowFragment$data[number]["alternativeTriageEntry"] | null
  completedAt?: Date | null
  timeWindowId?: string | null
  involvesPreventionProgram?: boolean
}

interface ResultType {
  ongoing: AppointmentAgenda[]
  planned: AppointmentAgenda[]
  past: AppointmentAgenda[]
}

type AgendaParams = {
  timeWindows: ReadonlyArray<TimeWindowsType>
  appointments: AppointmentEdges
  alternativeTriages: DeAlternativeTriageEdges | ChAlternativeTriageEdges
  externalAssistances?: ChExternalAssistancesEdges
}

const mergeAgendaAndAppointment = (
  { timeWindows, appointments, alternativeTriages, externalAssistances }: AgendaParams,
  patient: PatientFragment$data,
) => {
  const appointmentAgendaList: AppointmentAgenda[] = []

  if (timeWindows) {
    timeWindows.forEach((agenda) => {
      const {
        referralToCareProvider,
        referralReason,
        involvesPreventionProgram,
        id,
        __typename,
        referralToCareProviderId,
        referredBy,
        referralToDoctorName,
        errorCorrection,
        referenceDate,
        internalComment,
        referredByExternalCareProviderId,
        updatedAt,
        updatedBy,
        createdAt,
        createdBy,
        progressNoteId,
        alternativeTriage,
        alternativeTriageEntry,
      } = agenda

      const dateFrom = startOfDay(new Date(agenda?.dateFrom))
      const dateTo = startOfDay(new Date(agenda?.dateTo))
      const assignedTo = referralToCareProvider ? combineCareProviderTitle(referralToCareProvider) : ""

      const newAppointmentAgenda: AppointmentAgenda = {
        __typename,
        dateFrom,
        dateTo,
        dateSort: new Date(dateFrom),
        assignedTo,
        referralToCareProvider,
        referredBy,
        referralToCareProviderId: referralToCareProviderId ?? "",
        referralToDoctorName: referralToDoctorName ?? "",
        referralReason: referralReason,
        internalComment: internalComment ?? "",
        referredByExternalCareProviderId: referredByExternalCareProviderId ?? "",
        updatedAt: updatedAt ?? "",
        updatedBy: updatedBy ?? "",
        createdAt: createdAt ?? "",
        createdBy: createdBy ?? "",
        id,
        progressNoteId,
        errorCorrection: errorCorrection,
        referenceDate: referenceDate ? formatDate(referenceDate, "yyyy-MM-dd") : undefined,
        alternativeTriage,
        alternativeTriageEntry,
        involvesPreventionProgram: involvesPreventionProgram,
      }

      appointmentAgendaList.push(newAppointmentAgenda)
    })
  }

  appointments?.edges?.forEach(({ node }) => {
    const {
      id,
      delay,
      channel,
      assignedTo,
      reason,
      status,
      date,
      slotId,
      takenAt,
      language,
      access,
      service,
      specialty,
      __typename,
    } = node

    const time = getSlotLabel(slotId)

    const newAppointmentAgenda: AppointmentAgenda = {
      __typename,
      assignedTo,
      id,
      delay,
      reason,
      language,
      access,
      service,
      specialty,
      channel,
      patient,
      appointment: node,
      status,
      dateSort: status === "OPEN" ? new Date(`${date} ${time}`) : takenAt ? new Date(takenAt) : null,
    }
    appointmentAgendaList.push(newAppointmentAgenda)
  })

  alternativeTriages?.edges?.forEach(({ node }) => {
    const { __typename, id, createdAt, language, phoneNumber, reason, createdBy } = node
    const timeWindowId = "timeWindowId" in node ? node.timeWindowId : null
    const newAppointmentAgenda: AppointmentAgenda = {
      __typename,
      reason,
      id,
      language,
      phoneNumber,
      createdBy,
      createdAt,
      timeWindowId,
      dateSort: new Date(createdAt ?? ""),
    }
    appointmentAgendaList.push(newAppointmentAgenda)
  })

  externalAssistances?.edges?.forEach(({ node }) => {
    const { __typename, id, createdAt, language, phoneNumber, reason, createdBy } = node
    const newAppointmentAgenda: AppointmentAgenda = {
      __typename,
      reason,
      id,
      language,
      phoneNumber,
      createdAt,
      createdBy,
      dateSort: new Date(createdAt ?? ""),
    }
    appointmentAgendaList.push(newAppointmentAgenda)
  })

  return appointmentAgendaList
}

const sortAgendaAndAppointment = (appointmentAgendaList: AppointmentAgenda[]) => {
  return appointmentAgendaList.sort((a, b) => {
    const dateA = a.dateSort ? a.dateSort.getTime() : 0
    const dateB = b.dateSort ? b.dateSort.getTime() : 0
    return dateB - dateA
  })
}

const categorizeAgendaAndAppointment = (appointmentAgendaList: AppointmentAgenda[]) => {
  const today = startOfDay(new Date())
  const appointmentAgenda: ResultType = { ongoing: [], planned: [], past: [] }

  appointmentAgendaList.forEach((item) => {
    //check if dateFrom and dateTo exist in agenda

    switch (item.__typename) {
      case "Appointment": {
        switch (item.status) {
          case "IN_PROGRESS":
            appointmentAgenda.ongoing.push(item)
            break
          case "OPEN":
            appointmentAgenda.planned.push(item)
            break
          case "CLOSED":
          case "CANCELLED":
            appointmentAgenda.past.push(item)
            break
          case undefined:
            break
        }
        break
      }
      case "ch_TimeWindow": {
        if (item?.dateFrom && item?.dateTo) {
          const dateFrom = startOfDay(new Date(item?.dateFrom))
          const dateTo = startOfDay(new Date(item?.dateTo))

          if (isBefore(dateTo, today)) appointmentAgenda.past.push(item)
          else if (isAfter(dateFrom, today)) appointmentAgenda.planned.push(item)
          else appointmentAgenda.ongoing.push(item)
        }
        break
      }
      case "ch_AlternativeTriage":
      case "de_AlternativeTriage":
      case "ch_ExternalAssistance": {
        const completedAt = startOfDay(new Date(item?.createdAt ?? ""))
        if (isBefore(completedAt, today)) appointmentAgenda.past.push(item)
        else if (isAfter(completedAt, today)) appointmentAgenda.planned.push(item)
        else appointmentAgenda.past.push(item)
        break
      }
    }
  })

  return appointmentAgenda
}

export const handleAgendaAndAppointment = (
  { timeWindows, appointments, alternativeTriages, externalAssistances }: AgendaParams,
  patient: PatientFragment$data,
) => {
  const merge = mergeAgendaAndAppointment(
    { timeWindows, appointments, alternativeTriages, externalAssistances },
    patient,
  )
  const sort = sortAgendaAndAppointment(merge)
  return categorizeAgendaAndAppointment(sort)
}
